import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCases,
  updateCase,
  createCase,
  deleteCase,
  fetchCasesStats,
  caseWriteFailed,
  caseWriteErrorMessage,
} from "../api/case";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  case: {},
  totalCount: 0,
  currentCount: 0,
  newestCasesCount: 0,

  // weekly_total: 0,
  activity_monthly: [],
  monthly_total: [],
  new_today: 0,
  new_this_week: 0,
  new_this_month: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 200,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteCaseAsync = createAsyncThunk(
  "cases/deleteCaseAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteCase(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "delete_case");
      }
      // include the ID in the response
      if (response && typeof response === "object") {
        response.id = payload.id;
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_case");
    }
  }
);

export const createCaseAsync = createAsyncThunk(
  "cases/createCaseAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCase(payload);
      if (caseWriteFailed(response)) {
        const body = response && typeof response === "object" ? response : {};
        return rejectNormalized(
          rejectWithValue,
          {
            ...body,
            error: true,
            errors: [caseWriteErrorMessage(body, "Request failed")],
          },
          "create_case"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_case");
    }
  }
);

export const updateCaseAsync = createAsyncThunk(
  "cases/updateCaseAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCase(payload);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "update_case");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_case");
    }
  }
);

export const getCasesAsync = createAsyncThunk(
  "cases/getCasesAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchCases(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_cases");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_cases");
    }
  }
);

export const getCasesStatsAsync = createAsyncThunk(
  "cases/getCasesStatsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchCasesStats();
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "cases_stats");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "cases_stats");
    }
  }
);

export const loadMoreCasesAsync = createAsyncThunk(
  "cases/loadMoreCasesAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchCases(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "load_more_cases");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "load_more_cases");
    }
  }
);

const caseSlice = createSlice({
  name: "cases",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCasesAsync.pending, (state) => {
        state.isLoading = true;
        state.data = [];
        state.totalCount = 0;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCasesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestCasesCount = action.payload.newestCasesCount;
      })
      .addCase(getCasesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente sakene.");
      })
      .addCase(updateCaseAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateCaseAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = draftState.data.map((item) => {
            if (item.id === action.payload.id) {
              return action.payload;
            } else {
              return item;
            }
          });

          // draftState.case = action.payload;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateCaseAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere saken.");
      })
      .addCase(createCaseAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createCaseAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createCaseAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette saken.");
      })
      .addCase(deleteCaseAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteCaseAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = draftState.data.filter(
            (item) => item.id !== action.payload.id
          );
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(deleteCaseAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette saken.");
      })
      .addCase(loadMoreCasesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(loadMoreCasesAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = [...draftState.data, ...action.payload.data];
          draftState.totalCount = action.payload.totalCount;
          draftState.currentCount = draftState.data.length;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(loadMoreCasesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente flere saker.");
      })
      .addCase(getCasesStatsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCasesStatsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.weekly_total = action.payload.data.weekly_total;
        state.monthly_total = action.payload.data.monthly_total;
        state.new_today = action.payload.data.new_today;
        state.new_this_week = action.payload.data.new_this_week;
        state.new_this_month = action.payload.data.new_this_month;
        state.activity_monthly = action.payload.data.activity_monthly;
      })
      .addCase(getCasesStatsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente sakstatistikk."
        );
      });
  },
});

// export const {} = customerSlice.actions;

export const { reducer } = caseSlice;

export default caseSlice;
